/**
 * `useSearch` — React hook wiring a manifest to a {@link SearchClient}.
 *
 * Rebuilds the index whenever the manifest changes; exposes a debounced-free
 * async `search` plus a `ready` flag. The client is injectable for tests.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import { buildSearchRecords, type SearchHit } from './searchRecords.js';
import { createMainThreadSearchClient, type SearchClient } from './searchClient.js';

export interface UseSearchResult {
  ready: boolean;
  size: number;
  search: (query: string) => Promise<SearchHit[]>;
}

/**
 * @param manifest        the loaded manifest, or null while loading
 * @param clientFactory   injectable for tests; defaults to the main-thread client
 *                        (the production hook is wired with the worker client
 *                        by the caller via this same parameter)
 */
export function useSearch(
  manifest: AtlasManifest | null,
  clientFactory: () => SearchClient = createMainThreadSearchClient,
): UseSearchResult {
  const [ready, setReady] = useState(false);
  const [size, setSize] = useState(0);
  // The client (and its Worker) is created AND disposed inside this one effect.
  // Splitting creation into a useMemo + disposal into a separate effect breaks
  // under React StrictMode: the dev mount→unmount→mount cycle disposes the
  // memoised client (terminating its Worker), then reuses that dead client for
  // indexing — a postMessage to a terminated Worker is silently dropped, so the
  // index() promise never resolves and `ready` stays false forever. Owning the
  // whole lifecycle in one effect yields a balanced create→dispose→create.
  const clientRef = useRef<SearchClient | null>(null);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    if (!manifest) {
      clientRef.current = null;
      return;
    }
    const client = clientFactory();
    clientRef.current = client;
    const records = buildSearchRecords(manifest);
    void client.index(records).then((count) => {
      if (cancelled) return;
      setSize(count);
      setReady(true);
    });
    return () => {
      cancelled = true;
      client.dispose();
      if (clientRef.current === client) clientRef.current = null;
    };
  }, [manifest, clientFactory]);

  const search = useCallback(async (query: string): Promise<SearchHit[]> => {
    return clientRef.current ? clientRef.current.query(query) : [];
  }, []);

  return { ready, size, search };
}
